import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Download, Filter, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MODE_LABELS = {
  set: { sk: 'Nastaviť', ru: 'Установить', en: 'Set', cls: 'bg-blue-100 text-blue-800' },
  add: { sk: 'Pripočítať', ru: 'Прибавить', en: 'Add', cls: 'bg-green-100 text-green-800' },
  subtract: { sk: 'Odpočítať', ru: 'Вычесть', en: 'Subtract', cls: 'bg-amber-100 text-amber-800' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const AdminStockAdjustments = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'sk').split('-')[0];
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [productSearch, setProductSearch] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [mode, setMode] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [limit, setLimit] = useState(50);
  const [skip, setSkip] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      params.set('skip', String(skip));
      if (adminEmail) params.set('admin_email', adminEmail);
      if (mode) params.set('mode', mode);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);

      const token = localStorage.getItem('sunify_token');
      const res = await fetch(`${API_URL}/api/admin/stock-adjustments?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Fetch failed');
      const data = await res.json();
      let rows = data.items || [];
      if (productSearch.trim()) {
        const q = productSearch.trim().toLowerCase();
        rows = rows.filter((r) => (r.product_sku || '').toLowerCase().includes(q));
      }
      setItems(rows);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(t('admin.stock_history.fetch_error', 'Chyba pri načítaní'));
    } finally {
      setLoading(false);
    }
  }, [limit, skip, adminEmail, mode, dateFrom, dateTo, productSearch, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const exportCsv = () => {
    setExporting(true);
    try {
      const headers = ['Date', 'SKU', 'Mode', 'Old', 'New', 'Delta', 'Reason', 'Note', 'Admin'];
      const lines = [headers.join(',')];
      items.forEach((r) => {
        const row = [
          r.created_at || '',
          r.product_sku || '',
          r.mode || '',
          r.old_stock ?? '',
          r.new_stock ?? '',
          r.delta_stock ?? '',
          (r.reason || '').replace(/[",\n]/g, ' '),
          (r.note || '').replace(/[",\n]/g, ' '),
          r.actor_email || '',
        ];
        lines.push(row.map((v) => `"${v}"`).join(','));
      });
      const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stock-adjustments-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => {
    setProductSearch('');
    setAdminEmail('');
    setMode('');
    setDateFrom('');
    setDateTo('');
    setSkip(0);
  };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6" data-testid="stock-adjustments-title">
        {t('admin.stock_history.title', 'História korekcií skladu')}
      </h1>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-3 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              placeholder={t('admin.stock_history.search_sku', 'Hľadať SKU...')}
              className="w-full pl-10 pr-3 py-2 border rounded-lg"
              data-testid="stock-history-search"
            />
          </div>
          <div className="md:col-span-3">
            <input
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder={t('admin.stock_history.admin_email', 'E-mail admina')}
              className="w-full px-3 py-2 border rounded-lg"
              data-testid="stock-history-admin"
            />
          </div>
          <div className="md:col-span-2">
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              data-testid="stock-history-mode"
            >
              <option value="">{t('admin.stock_history.all_modes', 'Všetky režimy')}</option>
              <option value="set">{MODE_LABELS.set[lang] || MODE_LABELS.set.en}</option>
              <option value="add">{MODE_LABELS.add[lang] || MODE_LABELS.add.en}</option>
              <option value="subtract">{MODE_LABELS.subtract[lang] || MODE_LABELS.subtract.en}</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              data-testid="stock-history-date-from"
            />
          </div>
          <div className="md:col-span-2">
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              data-testid="stock-history-date-to"
            />
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between gap-2">
          <button
            onClick={resetFilters}
            className="text-sm text-gray-600 hover:text-gray-900 underline flex items-center gap-1"
            data-testid="stock-history-reset"
          >
            <Filter className="h-3.5 w-3.5" />
            {t('admin.stock_history.reset', 'Resetovať filtre')}
          </button>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">
              {t('admin.stock_history.total', 'Spolu')}: <strong>{total}</strong>
            </span>
            <button
              onClick={exportCsv}
              disabled={exporting || items.length === 0}
              className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
              data-testid="stock-history-export"
            >
              <Download className="h-4 w-4" />
              {t('admin.stock_history.export_csv', 'Export CSV')}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-gray-500" data-testid="stock-history-empty">
            {t('admin.stock_history.empty', 'Žiadne záznamy')}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left">{t('admin.stock_history.col_date', 'Dátum')}</th>
                <th className="px-4 py-3 text-left">{t('admin.stock_history.col_sku', 'SKU')}</th>
                <th className="px-4 py-3 text-left">{t('admin.stock_history.col_mode', 'Režim')}</th>
                <th className="px-4 py-3 text-right">{t('admin.stock_history.col_old', 'Pôvodné')}</th>
                <th className="px-4 py-3 text-right">{t('admin.stock_history.col_new', 'Nové')}</th>
                <th className="px-4 py-3 text-right">{t('admin.stock_history.col_delta', 'Delta')}</th>
                <th className="px-4 py-3 text-left">{t('admin.stock_history.col_reason', 'Príčina')}</th>
                <th className="px-4 py-3 text-left">{t('admin.stock_history.col_admin', 'Admin')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r, idx) => {
                const ml = MODE_LABELS[r.mode] || MODE_LABELS.set;
                const delta = Number(r.delta_stock || 0);
                return (
                  <tr key={idx} className="border-t hover:bg-gray-50" data-testid={`stock-history-row-${idx}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">{formatDate(r.created_at)}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.product_sku || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${ml.cls}`}>
                        {ml[lang] || ml.en}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{Number(r.old_stock || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">{Number(r.new_stock || 0).toFixed(2)}</td>
                    <td className={`px-4 py-3 text-right font-mono font-semibold ${delta < 0 ? 'text-red-600' : delta > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                      {delta > 0 ? '+' : ''}{delta.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <div className="truncate" title={r.reason}>{r.reason || '—'}</div>
                      {r.note && <div className="text-xs text-gray-500 truncate" title={r.note}>{r.note}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{r.actor_email || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <div className="text-gray-600">
            {skip + 1}–{Math.min(skip + limit, total)} {t('admin.stock_history.of', 'z')} {total}
          </div>
          <div className="flex gap-2">
            <button
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - limit))}
              className="px-3 py-1.5 border rounded disabled:opacity-30"
              data-testid="stock-history-prev"
            >
              ← {t('admin.stock_history.prev', 'Predošlé')}
            </button>
            <button
              disabled={skip + limit >= total}
              onClick={() => setSkip(skip + limit)}
              className="px-3 py-1.5 border rounded disabled:opacity-30"
              data-testid="stock-history-next"
            >
              {t('admin.stock_history.next', 'Ďalšie')} →
            </button>
            <select
              value={limit}
              onChange={(e) => { setLimit(Number(e.target.value)); setSkip(0); }}
              className="px-2 py-1.5 border rounded"
              data-testid="stock-history-limit"
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStockAdjustments;
